import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'

import { getPrivacyRegulation, getSwCountryCode } from '@/actions/region'
import {
  getServiceDecision,
  getTcString,
  getUSPData,
} from '@/containers/Usercentrics/usercentricsUtils'
import {
  getAppBundleName,
  getIfa,
  getLat,
  getModelName,
  platformManufacturer,
} from '@/platform/platform'
import { ResolvedAdSettings } from '@/types/servicesTypes/configTypes'
import { isWebos } from '@/utils/utils'

const eightDigitNumber = {
  min: 10000000,
  max: 100000000,
}

export const appstoreUrl = {
  tizen:
    'https://seller.samsungapps.com/tv/tizen-application/distribute/3201903018105',
  lg: 'https://us.lgappstv.com/main/tvapp/detail?appId=1233595',
}

const getRandomInt = ({ min, max }: { min: number; max: number }) => {
  const minCeiled = Math.ceil(min)
  const maxFloored = Math.floor(max)
  return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled)
}

const getUrl = (encoded = false) => {
  const url = window.location.href
  return encoded ? encodeURIComponent(url) : url
}

const getAppStoreUrl = () => {
  return isWebos() ? appstoreUrl.lg : appstoreUrl.tizen
}

export const getCMPStatus = () => {
  const CMPServiceDecision = getServiceDecision(
    'Usercentrics Consent Management Platform',
  )
  return CMPServiceDecision ? '1' : '0'
}

export const getGDPR = () => {
  const privacyRegulation = getPrivacyRegulation()
  return privacyRegulation === 'Tcf' ? '1' : '0'
}

export const getGDPRConsent = () => {
  const privacyRegulation = getPrivacyRegulation()
  return privacyRegulation === 'Tcf' ? getTcString() : undefined
}

const getUsPrivacy = () => {
  const privacyRegulation = getPrivacyRegulation()
  if (privacyRegulation !== 'Ccpa') {
    return undefined
  }
  return getUSPData()
}

export type UpdatedVideoPropsType = {
  url?: string
  isMuted?: boolean
  isAutoplay?: boolean
  isFullscreen?: boolean
  isVod?: boolean
}

const enum videoParams {
  DNT = 'dnt', // same as the lmt
  GDPR_CONSENT = 'gdpr_consent', // privacyRegulation === 'Tcf' ? getTcString : undefined
  AP = 'ap', // Autoplay: autoplay = 1 | not autoplay = 0
  URL = 'url', // pageUrl
  ENCODED_URL = 'encoded_url', // encoded pageUrl
  CB = 'cb', // random generated number, 8 numbers
  MUTE = 'mute', // Is player mute: muted = 1 | not muted = 0
  DID = 'did', // user device id for all device types
  APP_BUNDLE = 'app_bundle',
  APP_NAME = 'app_name',
  APP_STORE_URL = 'app_store_url',
  GDPR = 'gdpr', // privacyRegulation === 'Tcf' ? '1' : '0'
  US_PRIVACY = 'us_privacy', //privacyRegulation === 'Ccpa' ? getUSPData : undefined
  LMT = 'lmt', // tracking and measurement disabled = 1 | 0  (Get value from OS API)
  IFA_TYPE = 'ifa_type',
  AD_POS = 'ad_pos', // fullscreen = 7 | not fullscreen = 0
  AD_POSITION = 'ad_position', // fullscreen = 7 | not fullscreen = 0
  UA = 'ua', // useragent
  DEVICE_MAKE = 'device_make', // manufacturer
  BRAND_NAME = 'brand_name', // same as DEVICE_MAKE - manufacturer
  DEVICE_MODEL = 'device_model',
  COUNTRY = 'country',
  W = 'w',
  H = 'h',
  CONTENT_LIVESTREAM = 'content_livestream',
}

export const replaceVideoUrlParams = ({
  url = '',
  isMuted = false,
  isAutoplay = true,
  isFullscreen = true,
  isVod = false,
}: UpdatedVideoPropsType) => {
  if (!url.includes('?')) {
    return url
  }

  /**
   * Allow unknown search params
   * Will result in unhandled param=REPLACE_ME in the url
   */
  const allowUnknownSearchParams = true

  const urlObj = new URL(url)
  const searchParams = Object.fromEntries(urlObj.searchParams)

  for (const [key, value] of Object.entries(searchParams)) {
    if (value === 'REPLACE_ME' || value === 'REPLACEME') {
      const updatedValue = getUpdatedParams(
        key,
        isMuted,
        isAutoplay,
        isFullscreen,
        isVod,
      )
      searchParams[key] = updatedValue || ''
    }
  }

  const filteredSearchParams = Object.entries(searchParams).filter(
    (el) => el[1] !== 'REPLACE_ME' && el[1] !== 'REPLACEME',
  )

  let updatedSearchParams: string

  if (allowUnknownSearchParams) {
    updatedSearchParams = Object.entries(searchParams)
      .map((el) => el.join('='))
      .join('&')
  } else {
    updatedSearchParams = filteredSearchParams
      .map((el) => el.join('='))
      .join('&')
  }

  isDevMode &&
    console.log('[replaceVideoUrlParams] result', updatedSearchParams)
  return urlObj.origin + urlObj.pathname + '?' + updatedSearchParams
}

export const getUpdatedParams = (
  videoSearchParam: string,
  isMuted: boolean,
  isAutoplay: boolean,
  isFullscreen: boolean,
  isVod: boolean,
) => {
  switch (videoSearchParam) {
    case videoParams.DNT: {
      return getLat() ? '1' : '0'
    }
    case videoParams.GDPR_CONSENT: {
      return getGDPRConsent()
    }
    case videoParams.AP: {
      return isAutoplay ? '1' : '0'
    }
    case videoParams.AD_POS:
    case videoParams.AD_POSITION: {
      return isFullscreen ? '7' : '0'
    }
    case videoParams.URL: {
      return getUrl()
    }
    case videoParams.ENCODED_URL: {
      return getUrl(true)
    }
    case videoParams.CB: {
      return getRandomInt(eightDigitNumber).toString()
    }
    case videoParams.MUTE: {
      return isMuted ? '1' : '0'
    }
    case videoParams.DID: {
      return getIfa()
    }
    case videoParams.APP_BUNDLE: {
      return getAppBundleName()
    }
    case videoParams.APP_NAME: {
      return 'Sportworld'
    }
    case videoParams.APP_STORE_URL: {
      return getAppStoreUrl()
    }
    case videoParams.GDPR: {
      return getGDPR()
    }
    case videoParams.US_PRIVACY: {
      return getUsPrivacy()
    }
    case videoParams.LMT: {
      return getLat() ? '1' : '0'
    }
    case videoParams.IFA_TYPE: {
      return isWebos() ? 'lgudid' : 'tifa'
    }
    case videoParams.UA: {
      return window.navigator.userAgent
    }
    case videoParams.DEVICE_MAKE:
    case videoParams.BRAND_NAME: {
      return platformManufacturer.toLocaleLowerCase()
    }
    case videoParams.DEVICE_MODEL: {
      return getModelName()
    }
    case videoParams.COUNTRY: {
      return getSwCountryCode()
    }
    case videoParams.W: {
      return '1920'
    }
    case videoParams.H: {
      return '1080'
    }
    case videoParams.CONTENT_LIVESTREAM: {
      return isVod ? '0' : '1'
    }
    case 'test_csai_param': {
      return 'Done_csai'
    }
    case 'test_ssai_param': {
      return 'Done_ssai'
    }
    default: {
      break
    }
  }
}

/**
 * Server can provide csaiSettings: {} instead of csaiSettings: null
 * @param videoSrc
 * @param adSettings
 * @returns
 */
export const addSsaiParams = (
  videoSrc: string,
  adSettings?: Nullable<ResolvedAdSettings>,
) => {
  isDevMode && console.log('[VideoParamsUtils] AddSsaiParams')
  if (adSettings?.ssaiUrlParameters) {
    const ssaiUrlParameters = ['&', '?'].includes(
      adSettings.ssaiUrlParameters[0],
    )
      ? adSettings.ssaiUrlParameters.slice(1)
      : adSettings.ssaiUrlParameters
    isDevMode &&
      console.log(
        '[VideoParamsUtils] AddSsaiParams - ssai params are available. Start replace.',
      )
    const separator = videoSrc.includes('?') ? '&' : '?'
    const url = videoSrc + separator + ssaiUrlParameters
    return replaceVideoUrlParams({
      url: url,
      // url: 'https://amg00622-amg00622c7-sportworld-europe-2599.playouts.now.amagi.tv/playlist/amg00622-sportworld-de-sportworldeurope/playlist.m3u8?app_bundle=REPLACE_ME&app_name=REPLACE_ME&app_store_url=REPLACE_ME&genre=soccer&ic=IAB17&content_livestream=1&gdpr=REPLACE_ME&gdpr_consent=REPLACE_ME&url=REPLACE_ME&us_privacy=REPLACE_ME&lmt=REPLACE_ME&dnt=REPLACE_ME&cb=REPLACE_ME&ap=REPLACE_ME&mute=REPLACE_ME&encoded_url=REPLACE_ME&coppa=0&ifa_type=REPLACE_ME',
      // url: 'https://amg00622-amg00622c7-sportworld-europe-2599.playouts.now.amagi.tv/playlist/amg00622-sportworld-de-sportworldeurope/playlist.m3u8',
      isMuted: false,
      isAutoplay: false,
      isFullscreen: true,
    })
  } else {
    isDevMode &&
      console.log(
        '[VideoParamsUtils] AddSsaiParams - no ssai params available, skip',
      )
  }
  return videoSrc
}
